/**
 * The promo-code status vocabulary and the client-side list operations the
 * Promo Codes page (GOTU-5661) and the office Discounts & Promo Codes tab
 * (GOTU-6663) share.
 *
 * Why this lives here and not on the server: campaign-service's list filter is
 * the *lifecycle* status (draft / active / paused / closing / ended), but the ticket
 * defines status by the date window — Pending before it opens, Active inside
 * it, Expired after it closes — and has no search parameter at all. Both
 * views therefore load the whole (small) set of discount campaigns and derive,
 * search and page here. When campaign counts outgrow that, the fix is a
 * `search` param and a date-derived status filter on the service, and this
 * file is the one place to swap them in.
 */

/** Ticket-defined, date-derived statuses. */
export const PROMO_CODE_STATUSES = ['pending', 'active', 'paused', 'closing', 'expired'];

export const PROMO_CODE_STATUS_LABELS = {
  pending: 'Pending',
  active: 'Active',
  paused: 'Paused',
  closing: 'Closing',
  expired: 'Expired',
};

/**
 * Design-system `Label` severities. Active is green and Expired red as the
 * Figma shows; Pending, Paused and Closing have no mockup and take the neutral
 * informational tints so none reads as an alarm. Closing shares Paused's
 * tint: both mean "not taking new redemptions, but not gone".
 */
export const PROMO_CODE_STATUS_SEVERITY = {
  pending: 'info',
  active: 'success',
  paused: 'warning',
  closing: 'warning',
  expired: 'error',
};

export const getPromoCodeStatusSeverity = (status) =>
  PROMO_CODE_STATUS_SEVERITY[status] ?? 'info';

/**
 * The list shows 15 rows a page (ticket), paged here rather than on the wire
 * because the search and status filters are client-side too — see the file doc.
 */
export const PROMO_CODES_PAGE_SIZE = 15;

const toTime = (value) => {
  if (!value) return null;
  const time = new Date(value).getTime();

  return Number.isNaN(time) ? null : time;
};

/**
 * A code's status as the ticket defines it: by the date window, with the
 * lifecycle allowed to make it *less* live but never more.
 *
 * - `ended` is Expired whatever the dates say — an admin ended it early.
 * - `paused` is its own status. Folding it into Active would tell ops a code
 *   applies when the service is declining every claim; folding it into Expired
 *   would say it is gone when it can be resumed. Same call the Campaigns tab
 *   made for the same enum.
 * - `closing` (GOTU-8216) is its own status for the same reason: the service
 *   admits nobody new — every claim predicate tests `status = 'active'` — so
 *   Active would be wrong, yet it can still be reopened, so Expired would be
 *   too.
 * - `draft` is Pending: it has never been activated, so nothing can redeem it
 *   even inside its window.
 * - Otherwise: before `startsAt` is Pending, past `endsAt` is Expired, and a
 *   null `endsAt` never expires.
 *
 * `now` is injectable so the boundary cases are testable.
 */
export const derivePromoCodeStatus = (
  { campaignStatus, startsAt, endsAt },
  now = Date.now(),
) => {
  if (campaignStatus === 'ended') return 'expired';
  if (campaignStatus === 'paused') return 'paused';
  if (campaignStatus === 'closing') return 'closing';
  if (campaignStatus === 'draft') return 'pending';

  const start = toTime(startsAt);
  const end = toTime(endsAt);

  if (start !== null && now < start) return 'pending';
  if (end !== null && now > end) return 'expired';

  return 'active';
};

const normalise = (value) => String(value ?? '').trim().toLowerCase();

/**
 * Search by name or code (the ticket asks for code name; the Figma shows both
 * columns and ops know codes by either). Case- and whitespace-insensitive,
 * substring match. An empty search matches everything.
 */
export const matchesPromoCodeSearch = (row, search) => {
  const needle = normalise(search);
  if (!needle) return true;

  return normalise(row.name).includes(needle) || normalise(row.code).includes(needle);
};

/**
 * Applies the page's two filters. An empty `status` means every status.
 * Order is preserved — the rows arrive newest-first from the service.
 */
export const filterPromoCodes = (rows, { search = '', status = '' } = {}) =>
  rows.filter(
    (row) => matchesPromoCodeSearch(row, search) && (!status || row.status === status),
  );

/** One 1-based page of an already-filtered list. */
export const paginatePromoCodes = (rows, page = 1, pageSize = PROMO_CODES_PAGE_SIZE) => {
  const safePage = Math.max(1, Number(page) || 1);
  const start = (safePage - 1) * pageSize;

  return rows.slice(start, start + pageSize);
};
